import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const MIN_PASSWORD = 8;

// Set a new password (MEA-160). Public route: /update-password. Reached from the
// reset email, which supabase-js turns into a recovery session (PASSWORD_RECOVERY)
// so updateUser has an authenticated context. Rendered outside the app shell so
// it works regardless of household state.
export default function UpdatePassword() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    const { error } = await updatePassword(password);
    setSubmitting(false);
    if (error) {
      // Most common cause: the recovery link expired or was already used, so
      // there's no session for updateUser to act on.
      setError(
        error.message?.includes('session')
          ? 'Your reset link has expired. Request a new one from the sign-in page.'
          : error.message
      );
      return;
    }
    setDone(true);
  };

  return (
    <div className="min-h-screen bg-canvas text-ink flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/dinner-league-icon-192.png" alt="" aria-hidden="true" className="w-14 h-14 mx-auto mb-4 rounded-btn shadow-glow-sm" />
          <h1 className="font-display text-2xl font-semibold text-ink mb-2">Set a new password</h1>
        </div>

        {done ? (
          <div className="rounded-panel border border-hairline p-6 text-center" style={{ background: '#0d1220' }}>
            <div className="text-4xl mb-3">✅</div>
            <h3 className="font-display font-bold text-ink mb-3">Password updated</h3>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 rounded-btn bg-lime-gradient text-canvas font-display font-bold shadow-glow hover:brightness-110 transition"
            >
              Continue
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-panel border border-hairline p-6" style={{ background: '#0d1220' }}>
            <label htmlFor="password" className="block font-display text-xs font-bold text-ink-soft mb-1.5">
              New password
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={`At least ${MIN_PASSWORD} characters`}
              minLength={MIN_PASSWORD}
              className="w-full px-4 py-3 rounded-btn border border-hairline font-body text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-lime/60 focus:border-transparent transition"
              style={{ background: '#0a1220' }}
            />
            <label htmlFor="confirm" className="block font-display text-xs font-bold text-ink-soft mb-1.5 mt-4">
              Confirm password
            </label>
            <input
              id="confirm"
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-btn border border-hairline font-body text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-lime/60 focus:border-transparent transition"
              style={{ background: '#0a1220' }}
            />
            {error && <p className="mt-3 font-body text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-5 py-3 rounded-btn bg-lime-gradient text-canvas font-display font-bold shadow-glow hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
